import { action } from 'typesafe-actions';

import { IKnmDisapproveRequestReason } from 'APIPackage/KnmModule/interfaces/IKnmDisapproveRequestReason';
import { IKnmMoveToPlanAttachment } from 'CorePackage/MainModule/interfaces/IKnmMoveToPlanAttachment';
import { IKnmListFilterParams } from 'APIPackage/KnmListModule/interfaces/IKnmListFilterParams';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { ExportSimilaritiesListTypesEnum } from 'APIPackage/SimilarityModule/enums/ExportSimilaritiesListTypesEnum';
import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { IKnmExclusionReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmExclusionReasonSelectOption';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { IKnmIndex } from 'APIPackage/KnmModule/interfaces/IKnmIndex';

import { KnmListActionsEnum } from '../enums/KnmListActionsEnum';
import { IKnmRaw } from '../interfaces/IKnmRaw';
import { ISelectOption } from '../../../UIPackage/MainModule/components/SelectInput';

export const knmListInitPage = () => action(KnmListActionsEnum.KNM_LIST_INIT_PAGE);
export const knmListSetSearchValue = (value: string) => action(KnmListActionsEnum.KNM_LIST_SET_SEARCH_VALUE, { value });
export const knmListGetData = (searchValue = '') => action(KnmListActionsEnum.KNM_LIST_GET_DATA, { searchValue });
export const knmListGetDataNextPage = () => action(KnmListActionsEnum.KNM_LIST_GET_DATA_NEXT_PAGE);
export const knmListSetData = (
  data: IKnmRaw[],
  page: number,
  pageCount: number,
  totalCount: number,
  rejected: number,
) => action(KnmListActionsEnum.KNM_LIST_SET_DATA, { data, page, pageCount, totalCount, rejected });
export const knmListSetOrder = (order: string | null) => action(KnmListActionsEnum.KNM_LIST_SET_ORDER, { order });
export const knmListDeleteKnms = (ids: number[]) => action(KnmListActionsEnum.KNM_LIST_DELETE_KNMS, { ids });
export const knmListRestoreKnms = (ids: number[]) => action(KnmListActionsEnum.KNM_LIST_RESTORE_KNMS, { ids });
export const knmListCancelKnms = (ids: number[], commentText?: string) =>
  action(KnmListActionsEnum.KNM_LIST_CANCEL_KNMS, { ids, commentText });
export const knmListDisapproveKnms = (ids: number[], rejectReasons: IKnmDisapproveRequestReason[]) =>
  action(KnmListActionsEnum.KNM_LIST_DISAPPROVE_KNMS, { ids, rejectReasons });
export const knmListUpdateKnmsStartDate = (ids: number[], startDate: string) =>
  action(KnmListActionsEnum.KNM_LIST_UPDATE_KNMS_START_DATE, { ids, startDate });
export const knmListUpdateKnmsDuration = (ids: number[], durationDays?: number, durationHours?: number) =>
  action(KnmListActionsEnum.KNM_LIST_UPDATE_KNMS_DURATION, { ids, durationDays, durationHours });
export const knmListRemoveKnmsFromPlan = (
  ids: string[],
  params?: IKnmMoveToPlanAttachment | IKnmExclusionReasonSelectOption,
) => action(KnmListActionsEnum.KNM_LIST_REMOVE_KNMS_FROM_PLAN, { ids, params });
export const knmListExportList = (
  notify: ({ id }: IExport) => void,
  format: ExportFormatsEnum,
  columns: ITableColumn<IKnmRaw>[],
  filter?: IKnmListFilterParams,
) => action(KnmListActionsEnum.KNM_LIST_EXPORT_LIST, { format, columns, filter, notify });
export const knmListExportSimilaritiesList = (
  notify: ({ id }: IExport) => void,
  id: string,
  format: Exclude<ExportFormatsEnum, ExportFormatsEnum.PDF>,
  type: ExportSimilaritiesListTypesEnum,
) => action(KnmListActionsEnum.KNM_LIST_EXPORT_SIMILARITIES_LIST, { format, id, type, notify });
export const knmListExportRegistryKnm = (
  notify: ({ id }: IExport) => void,
  id: number,
  type: keyof Omit<
    IKnmCheckSubjectResult,
    'requestTime' | 'isOrganizationNameError' | 'isFnsAddressError' | 'isWorkAddressError'
  >,
) => action(KnmListActionsEnum.KNM_LIST_EXPORT_REGISTRY_KNM, { id, type, notify });
export const knmListFindKnm = (filterParam: IKnmListFilterParams, size?: number) =>
  action(KnmListActionsEnum.KNM_LIST_FIND_KNM, { filterParam, size });
export const knmListFindKnmForDecisionReason = (filterParam: IKnmListFilterParams, size?: number) =>
  action(KnmListActionsEnum.KNM_LIST_FIND_KNM_FOR_DECISION_REASON, { filterParam, size });
export const knmListSetFindData = (findData: IKnmIndex[]) =>
  action(KnmListActionsEnum.KNM_LIST_SET_FIND_DATA, { findData });
export const knmListRemoveFromPlanClick = (
  isAvailableKnms: boolean,
  filteredKnms: IKnmRaw[],
  openRemoveFromPlanModal: () => void,
) =>
  action(KnmListActionsEnum.KNM_LIST_REMOVE_FROM_PLAN_CLICK, {
    isAvailableKnms,
    filteredKnms,
    openRemoveFromPlanModal,
  });
export const knmListGetKnmSelectOptions = (
  searchValue: string,
  isErp: boolean,
  callback: (options: ISelectOption[]) => void,
) => action(KnmListActionsEnum.KNM_LIST_GET_KNM_SELECT_OPTIONS, { searchValue, isErp, callback });
export const hasKnmUnresolvedViolations = (knmId: string, callback: (hasUnresolvedViolations: boolean) => void) =>
  action(KnmListActionsEnum.HAS_KNM_UNRESOLVED_VIOLATIONS, { knmId, callback });
